<template>
  <Modal
    :modal-types="[CREATE_CHART_LIST_MODAL]"
    font-awesome-icon-class="fas fa-fw fa-plus-circle"
    modal-title="Create New ChartList"
    :should-close-modal-on-background-click="false"
  >
    <div class="create-chart-list-container">
      <form @submit.prevent="handleCreateChartList()">
        <div
          v-if="hasReachedMaximumNumberOfChartLists"
          class="chart-lists-usage-message"
        >You have used all {{ maximumNumberOfChartListsAllowed }} of your ChartLists.</div>
        <div
          v-if="hasReachedMaximumNumberOfChartLists && !isPro"
          class="account-upgrade-message"
        >
          Unlock more ChartLists when you upgrade your StockCharts service&nbsp;level
          <br />
          <a
            class="sc3-button sc3-button-blue sc3-button-medium"
            href="/youracct/servicelevel"
            target="_blank"
          >
            Upgrade Now
            <i class="fas fa-fw fa-arrow-circle-right"></i>
          </a>
        </div>
        <label
          v-show="!hasReachedMaximumNumberOfChartLists"
          class="sc3-form-label"
          for="create-chartlist-modal-name-input"
        >CHARTLIST NAME</label>
        <input
          v-show="!hasReachedMaximumNumberOfChartLists"
          v-model="createChartListName"
          type="text"
          placeholder="Name Your New ChartList"
          id="create-chartlist-modal-name-input"
          class="sc3-text-input"
          autocomplete="off"
          :disabled="isCreatingChartList"
        />
        <label
          v-show="!hasReachedMaximumNumberOfChartLists"
          class="sc3-form-label"
          for="create-chartlist-modal-symbols-input"
        >ADD SYMBOLS</label>
        <textarea
          v-show="!hasReachedMaximumNumberOfChartLists"
          v-model="createChartListSymbols"
          placeholder="Separate symbols with a comma or a line break"
          id="create-chartlist-modal-symbols-input"
          class="sc3-textarea"
          autocomplete="off"
          :disabled="isCreatingChartList"
        ></textarea>
        <hr class="sc3-form-divider" />
        <div v-if="errorMessage && !hasReachedMaximumNumberOfChartLists" class="error-message">{{ errorMessage }}</div>
        <button
          v-show="!hasReachedMaximumNumberOfChartLists"
          type="submit"
          class="sc3-button sc3-button-green"
          :disabled="isCreatingChartList"
        >
          <i v-show="!isCreatingChartList" class="fas fa-fw fa-check-circle"></i>
          <i v-show="isCreatingChartList" class="fad fa-fw fa-spinner-third fa-spin"></i>
          &nbsp;Create New ChartList
        </button>
        <button
          type="button"
          class="sc3-button-link"
          @click="closeModal()"
        >Close</button>
      </form>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useModals } from '@/composables/useModals';
  import { useAccountLevelsMaximumLimits } from '@/composables/useAccountLevelsMaximumLimits';
  import { addBulkSymbolsToChartList, createChartList } from '@/composables/createChartList';
  import { refreshChartLists } from '@/composables/refreshChartLists';
  import { getDefaultChartStyleStyleUrlSearchParams } from '@/composables/getDefaultChartStyleStyleUrlSearchParams';
  import Modal from '@/components/modals/Modal.vue';
  import { JsonLayerChartList } from '@/interfaces/json-layer-responses/jsonLayerChartList';

  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { modalType } = storeToRefs(layoutStore);
  const { isPro } = useAuthentication();
  const {
    addGreenActionResultMessage,
    addYellowActionResultMessage,
    addRedActionResultMessage
  } = useActionResultMessages();
  const { CREATE_CHART_LIST_MODAL, closeModal } = useModals();
  const { isAccountLevelsMaximumLimitsInitialized, maximumNumberOfChartListsAllowed } = useAccountLevelsMaximumLimits();
  const createChartListName = ref('');
  const createChartListSymbols = ref('');
  const errorMessage = ref('');
  const isCreatingChartList = ref(false);

  const hasReachedMaximumNumberOfChartLists = computed(() => {
    return (
      isAccountLevelsMaximumLimitsInitialized.value &&
      savedChartsStore.chartLists.filter(chartList => !chartList.isReadonly).length >= maximumNumberOfChartListsAllowed.value &&
      maximumNumberOfChartListsAllowed.value > 0
    );
  });

  async function handleCreateChartList() {
    if (modalType.value !== CREATE_CHART_LIST_MODAL) {
      isCreatingChartList.value = false;
      return;
    }

    clearErrorMessage();

    isCreatingChartList.value = true;
    const trimmedChartName = createChartListName.value.trim();

    if (!trimmedChartName) {
      errorMessage.value = 'ChartList name cannot be empty';
      isCreatingChartList.value = false;
      return;
    }

    if (trimmedChartName.length > 100) {
      errorMessage.value = 'ChartList name cannot be greater than 100 characters';
      isCreatingChartList.value = false;
      return;
    }

    let chartList: JsonLayerChartList;

    try {
      chartList = await createChartList(trimmedChartName);

      await refreshChartLists();
    } catch (e) {
      window.sc3WorkbenchLogger.error('Failed to create ChartList from CreateChartListModal - error occurred while creating', e);
      addRedActionResultMessage('Failed to create ChartList. Please try again.');

      isCreatingChartList.value = false;

      closeModal();
      return;
    }

    try {
      const symbols = sanitizeSymbols(createChartListSymbols.value);

      if (symbols) {
        const defaultChartStyleStyleUrlSearchParams = await getDefaultChartStyleStyleUrlSearchParams();

        await addBulkSymbolsToChartList(chartList.listNum.toString(), symbols, defaultChartStyleStyleUrlSearchParams);
      }

      addGreenActionResultMessage('Success! Your ChartList has been created!');
    } catch (e) {
      addYellowActionResultMessage('ChartList created successfully, but an error occurred while populating.');

      window.sc3WorkbenchLogger.error('Failed to add bulk symbols to ChartList from CreateChartListModal - error occurred', e);
    }

    isCreatingChartList.value = false;

    closeModal();
  }

  function sanitizeSymbols(rawSymbols: string) {
    return rawSymbols.replace(/\s/g, ',')
      .replace(/,+/g, ',') // reduce multiple commas to one comma
      .toUpperCase();
  }

  function resetModal() {
    errorMessage.value = '';
    createChartListName.value = '';
    createChartListSymbols.value = '';
    isCreatingChartList.value = false;
  }

  function handleModalTypeChange() {
    if (modalType.value !== CREATE_CHART_LIST_MODAL)
      return;

    resetModal();
  }


  function clearErrorMessage() {
    errorMessage.value = '';
  }

  watch(modalType, handleModalTypeChange);
  watch(createChartListSymbols, clearErrorMessage);
  watch(createChartListName, clearErrorMessage);
</script>

<style scoped>
  .create-chart-list-container {
    text-align: center;
  }

  .chart-lists-usage-message,
  .account-upgrade-message,
  .error-message {
    margin: 5px 0px;
    font-size: 14px;
    font-weight: 700;
    border-radius: 4px;
    line-height: 1.5;
    padding: 5px;
    border-width: 1px;
    border-style: solid;
  }

  .chart-lists-usage-message {
    border-color: #dfe7ec;
    color: #707f8f;
    background-color: #f2f5f7;
  }

  .account-upgrade-message {
    border-color: #bce8f1;
    color: #31708f;
    background-color: #d9edf7;
  }

  .error-message {
    border-color: #f5c6cb;
    color: #721c24;
    background-color: #a49798;
  }
</style>
